import React, { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { hasPermission } from '../utils/loginRestrictions';
import {
  needsRegistrationCompletion,
  readPendingRegistration,
  savePendingRegistration,
} from '../utils/registrationFlow';
import LoadingSpinner from './LoadingSpinner';

export default function PrivateRoute({ children, allowedRoles = [], requiredPermissions = [] }) {
  const { user, userData, loading, permissionsLoaded } = useContext(AuthContext);
  const location = useLocation();

  // Still loading auth state
  if (loading) return <LoadingSpinner />;

  // Not authenticated
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  // User exists but userData not yet loaded - wait for critical data (prevents premature render)
  if (!userData && user) return <LoadingSpinner />;

  // Keep pending registration creds for trial checkout, but do NOT wall users to select-plan.
  // They can explore the clinic shell; trial starts from the banner / Generate AI Consent.
  if (needsRegistrationCompletion(userData)) {
    const existing = readPendingRegistration();
    if (!existing?.email && user?.email) {
      savePendingRegistration({
        email: user.email,
        password: existing?.password || '',
        practiceName: userData.displayName || user.displayName || '',
      });
    }
  }

  // Check role-based access
  if (allowedRoles.length > 0 && !allowedRoles.includes(userData?.role)) {
    // Redirect to appropriate dashboard based on role
    if (userData?.role === 'super_admin') {
      return <Navigate to="/admin" replace />;
    } else if (userData?.role === 'patient') {
      return <Navigate to="/patient" replace />;
    } else {
      return <Navigate to="/clinic" replace />;
    }
  }

  // Check permission-based access
  if (requiredPermissions.length > 0 && !requiredPermissions.every(permission => hasPermission(userData, permission))) {
    return <Navigate to="/clinic" replace />;
  }

  return children;
}